import {
  BriefcaseIcon,
  EarthIcon,
  LockIcon,
  SettingsIcon,
} from '@carboncopyco/icons';
import {
  type WorkspaceColor,
  type WorkspaceNarrowType,
  type WorkspaceSettings,
  workspaceColorsMap,
} from '@carboncopyco/types';
import {
  Button,
  ButtonDropdownMenu,
  Form,
  InputTextField,
  LoadingDots,
  MessageBox,
  Modal,
  SubmitButton,
  TextBase,
  TextSubHeader,
} from '@carboncopyco/ui';
import { Field, type FieldProps } from 'formik';
import { object, string } from 'yup';
import { WorkspaceAvatar } from '../WorkspaceAvatar';

export type WorkspaceSettingsModalProps = {
  isOpen: boolean;
  handleSubmit: (settings: WorkspaceSettings) => void;
  handleClose: () => void;
  isLoading: boolean;
  zIndex?: number;
} & (
  | { settings?: never; isNew: true }
  | { settings: WorkspaceSettings; isNew?: false }
);

const workspaceSettingsSchema = object({
  workspaceName: string().defined('Please specify a workspace name'),
  workspaceType: string().defined(),
  color: string().defined(),
});

const workspaceTypes = {
  public: {
    id: 'public',
    label: 'Public',
    icon: <EarthIcon size={18} />,
    description: 'Visible and accessible to everyone in the account',
  },
  private: {
    id: 'private',
    label: 'Invite only',
    icon: <LockIcon size={18} />,
    description: 'Visible and accessible only for invited members',
  },
};

export const WorkspaceSettingsModal = ({
  isNew,
  isOpen,
  settings,
  handleSubmit,
  handleClose,
  isLoading,
  zIndex,
}: WorkspaceSettingsModalProps) => {
  const randomWorkspaceColor = Object.keys(workspaceColorsMap)[
    Math.floor(Math.random() * Object.keys(workspaceColorsMap).length)
  ] as WorkspaceColor;

  return (
    <Modal
      containerClass="w-ds-146"
      open={isOpen}
      onClose={handleClose}
      zIndex={zIndex}
      title={
        <div className="flex items-center">
          <div className="mr-ds-2.5">
            {isNew ? <BriefcaseIcon /> : <SettingsIcon />}
          </div>
          <TextSubHeader label={isNew ? 'Create workspace' : 'Edit settings'} />
        </div>
      }
    >
      {isLoading ? (
        <div className="flex items-center flex-col p-5 h-full mt-ds-24">
          <div className="mb-ds-24">
            <LoadingDots />
          </div>
          <TextBase
            color="text-ds-natural-600"
            label={`Please wait while ${isNew ? 'creating' : 'updating'} workspace`}
          />
        </div>
      ) : (
        <div className="h-full w-full flex flex-col items-stretch justify-between">
          <Form<WorkspaceSettings>
            initialValues={
              settings || {
                workspaceName: '',
                workspaceType: 'public',
                color: randomWorkspaceColor,
              }
            }
            validationSchema={workspaceSettingsSchema}
            onSubmit={(settings) => handleSubmit(settings)}
          >
            <div className="flex justify-center mt-ds-6">
              <Field name="color">
                {({ field, form }: FieldProps) => (
                  <WorkspaceAvatar
                    workspace={{
                      isDeleted: false,
                      workspaceName: form.values.workspaceName,
                      color: field.value,
                      workspaceType: 'public',
                    }}
                    size="xlarge"
                  />
                )}
              </Field>
            </div>
            <div className="mt-ds-6">
              <InputTextField
                noAddedHeight
                name="workspaceName"
                placeholder="Enter a workspace name"
              />
            </div>
            <div className="mt-ds-10 flex gap-2">
              <Field name="workspaceType">
                {({ field, form }: FieldProps) => {
                  const { onChange, value: type, ...rest } = field;
                  const workspaceType =
                    workspaceTypes[type as WorkspaceNarrowType] ||
                    workspaceTypes.public;

                  return (
                    <>
                      <ButtonDropdownMenu
                        aria-label="workspace settings dropdown"
                        primary={type !== 'default'}
                        disabled={type === 'default'}
                        value={{
                          id: type,
                          label: workspaceType.label,
                          icon: workspaceType.icon,
                        }}
                        items={Object.values(workspaceTypes)}
                        onSelect={(item) => {
                          form.setValues({
                            ...form.values,
                            workspaceType: item.id,
                          });
                        }}
                        {...rest}
                      />
                      <div className="flex-grow">
                        <MessageBox
                          fullHeight
                          label={workspaceType.description}
                        />
                      </div>
                    </>
                  );
                }}
              </Field>
            </div>
            <div className="flex items-center justify-end mt-ds-5 gap-2">
              <Button
                label="Cancel"
                link
                secondary
                onClick={handleClose}
              />
              <SubmitButton
                render={({ isValid }: { isValid: boolean }) => (
                  <Button
                    disabled={!isValid}
                    label={isNew ? 'Create workspace' : 'Save settings'}
                    submit
                  />
                )}
              />
            </div>
          </Form>
        </div>
      )}
    </Modal>
  );
};
